import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Send } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Reusable customer inquiry dialog. Pre-fills product_interest with the product
// name (or a list of cart items). Hits POST /api/inquiries which is auto-routed
// to the CRM as a "retail" inquiry.
export default function ProductInquiryDialog({ open, onClose, productName, cartItems }) {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    country: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const isCart = !!cartItems?.length;
  const productInterest = isCart
    ? `Cart inquiry: ${cartItems.map((i) => `${i.title} (qty ${i.quantity})`).join(", ")}`
    : productName || "Product inquiry";

  const reset = () => {
    setForm({ name: "", email: "", phone: "", country: "", message: "" });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/inquiries`, {
        ...form,
        inquiry_type: "wholesale",
        product_interest: productInterest,
        message: form.message || `Please contact me about: ${productInterest}`,
      });
      toast.success("Inquiry sent! Our team will contact you within 24 hours.");
      reset();
      onClose();
    } catch (err) {
      console.error(err);
      toast.error("Failed to send inquiry. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent
        className="max-w-md bg-[#0A0A0A] border border-[#C9A962]/30 text-[#F5F5F0]"
        data-testid="product-inquiry-dialog"
      >
        <DialogHeader>
          <DialogTitle
            className="text-2xl text-[#C9A962]"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            {isCart ? "Inquire About Your Cart" : "Enquire About This Product"}
          </DialogTitle>
          <DialogDescription className="text-[#8B7355] text-sm">
            Have questions, want a custom quote, or need certification details?
            Our team responds within 24 hours.
          </DialogDescription>
        </DialogHeader>

        <div className="px-4 py-3 bg-[#0F0F0F] border border-[#C9A962]/15 text-xs text-[#B8A88A]" data-testid="inquiry-product-context">
          <span className="text-[#8B7355] uppercase tracking-wider text-[10px] block mb-1">
            Regarding
          </span>
          {productInterest}
        </div>

        <form onSubmit={handleSubmit} className="space-y-3 mt-2">
          <input
            required
            type="text"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="Your name *"
            className="w-full bg-transparent border-b border-[#C9A962]/30 text-[#F5F5F0] py-2 placeholder-[#71717A] focus:outline-none focus:border-[#C9A962]"
            data-testid="inquiry-name"
          />
          <input
            required
            type="email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            placeholder="Email *"
            className="w-full bg-transparent border-b border-[#C9A962]/30 text-[#F5F5F0] py-2 placeholder-[#71717A] focus:outline-none focus:border-[#C9A962]"
            data-testid="inquiry-email"
          />
          <input
            required
            type="tel"
            value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
            placeholder="Phone *"
            className="w-full bg-transparent border-b border-[#C9A962]/30 text-[#F5F5F0] py-2 placeholder-[#71717A] focus:outline-none focus:border-[#C9A962]"
            data-testid="inquiry-phone"
          />
          <input
            required
            type="text"
            value={form.country}
            onChange={(e) => setForm({ ...form, country: e.target.value })}
            placeholder="Country *"
            className="w-full bg-transparent border-b border-[#C9A962]/30 text-[#F5F5F0] py-2 placeholder-[#71717A] focus:outline-none focus:border-[#C9A962]"
            data-testid="inquiry-country"
          />
          <textarea
            value={form.message}
            onChange={(e) => setForm({ ...form, message: e.target.value })}
            placeholder="Your question or requirements (optional)"
            rows="3"
            className="w-full bg-transparent border border-[#C9A962]/30 text-[#F5F5F0] p-2 placeholder-[#71717A] focus:outline-none focus:border-[#C9A962]"
            data-testid="inquiry-message"
          />
          <button
            type="submit"
            disabled={submitting}
            className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-[#C9A962] to-[#8B6914] text-[#0A0A0A] font-semibold hover:opacity-90 transition-all disabled:opacity-50"
            data-testid="inquiry-submit"
          >
            <Send className="w-4 h-4" />
            {submitting ? "Sending..." : "Send Inquiry"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
